import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("token");

const authHeaders = () => ({ token: getToken() });

/* GET /admin/warehouse?offset&limit&search&isActive */
export const getWarehouses = async ({ offset = 1, limit = 10, search = "", isActive = "" } = {}) => {
  const params = { offset, limit };
  if (search) params.search = search;
  if (isActive !== "") params.isActive = isActive;

  return axios.get(`${API_BASE_URL}/admin/warehouse`, {
    params,
    headers: authHeaders(),
  });
};

/* GET /admin/warehouse/:id */
export const getWarehouseById = async (id) => {
  return axios.get(`${API_BASE_URL}/admin/warehouse/${id}`, {
    headers: authHeaders(),
  });
};

/*
 * POST /admin/warehouse
 * payload: { name, description, dimensions: { length, width, height },
 *            uploadImages: [fileName], location, latitude, longitude }
 */
export const createWarehouse = async (payload) => {
  return axios.post(`${API_BASE_URL}/admin/warehouse`, payload, {
    headers: authHeaders(),
  });
};

/* PUT /admin/warehouse/:id (same payload as create) */
export const updateWarehouse = async (id, payload) => {
  return axios.put(`${API_BASE_URL}/admin/warehouse/${id}`, payload, {
    headers: authHeaders(),
  });
};

/* PUT /admin/warehouse/:id { isActive } — activate (true) or deactivate (false) */
export const setWarehouseStatus = async (id, isActive) => {
  return axios.put(
    `${API_BASE_URL}/admin/warehouse/${id}`,
    { isActive: Boolean(isActive) },
    { headers: authHeaders() },
  );
};

/*
 * DELETE /admin/warehouse/:id — permanent delete.
 * Refused by the API when an order already uses the warehouse
 * ("Warehouse cannot be deleted because an order already uses this warehouse.").
 */
export const deleteWarehouse = async (id) => {
  return axios.delete(`${API_BASE_URL}/admin/warehouse/${id}`, {
    headers: authHeaders(),
  });
};

// Some endpoints answer 200 with { status:false } / { success:false } instead of an HTTP error.
export const assertApiSuccess = (response, fallback) => {
  if (response?.data?.status === false || response?.data?.success === false) {
    throw new Error(getApiMessage(response, fallback));
  }
  return response;
};

/* -------- response helpers (the API wraps data in a few possible shapes) -------- */

export const extractWarehouseList = (response) => {
  const body = response?.data;
  const data = body?.data ?? body;
  const list =
    (Array.isArray(data?.warehouses) && data.warehouses) ||
    (Array.isArray(data?.records) && data.records) ||
    (Array.isArray(data?.data) && data.data) ||
    (Array.isArray(data) && data) ||
    [];

  const count = Number(
    data?.count ??
      data?.total ??
      data?.totalCount ??
      data?.pagination?.total ??
      body?.count ??
      body?.total ??
      list.length,
  );

  return { list, count: Number.isFinite(count) ? count : list.length };
};

// Orders that use this warehouse (returned by GET /admin/warehouse/:id).
export const extractWarehouseOrders = (response) => {
  const body = response?.data;
  const data = body?.data ?? body;
  const orders =
    (Array.isArray(data?.orders) && data.orders) ||
    (Array.isArray(data?.warehouse?.orders) && data.warehouse.orders) ||
    (Array.isArray(body?.orders) && body.orders) ||
    [];
  return orders;
};

export const extractWarehouse = (response) => {
  const body = response?.data;
  const data = body?.data ?? body;
  return data?.warehouse || (data?._id ? data : null);
};

export const getApiMessage = (responseOrError, fallback) =>
  responseOrError?.response?.data?.message ||
  responseOrError?.response?.data?.data?.message ||
  (typeof responseOrError?.response?.data?.data === "string" && responseOrError.response.data.data) ||
  responseOrError?.data?.message ||
  responseOrError?.data?.data?.message ||
  // our own thrown errors (assertApiSuccess) — not axios errors, whose message is generic
  (responseOrError instanceof Error && !responseOrError.isAxiosError && responseOrError.message) ||
  fallback;
